import { IssueReport } from '../../../api/types';
import { SearchableSelectOption } from '../../../components/ui/SearchableSelectField';
import { getIssueTypeText } from '../../issueReports/util/issueReportHelpers';
import { formatDateTime } from '../../../utils';

/**
 * Milliseconds a report was created at, or 0 when that can't be determined.
 *
 * Reports whose date is missing or unparseable sort last instead of throwing
 * off the whole ordering (`NaN` comparisons make `Array.sort` incoherent).
 */
const createdMillis = (report: IssueReport): number => {
    const time = report.createdAt ? new Date(report.createdAt).getTime() : NaN;
    return Number.isNaN(time) ? 0 : time;
};

/**
 * The booking number, but only when it actually identifies a booking.
 *
 * Reports exist whose stored booking number is a placeholder such as `-`,
 * which rendered as the useless label `#-`. Anything without a letter or a
 * digit is treated as absent so the row falls back to its creation date.
 */
const identifyingBookingNumber = (report: IssueReport): string | null => {
    const raw = report.bookingNumber?.trim() ?? '';
    return /[A-Za-z0-9]/.test(raw) ? raw : null;
};

interface BuildOptions {
    t: (key: string) => string;
    language: 'de' | 'en';
}

/**
 * Turn the problem-report list into options for the goodwill code's source
 * picker, newest first.
 *
 * Every field read here is one the shipped issue-reports list already renders,
 * so the response shape is exercised in production today: `createdAt` is
 * emitted by `getAllDamageReports` as `new Date(...).toISOString()` and is
 * already parsed for that list's sort order.
 *
 * Pure and separately tested — the picker's usefulness depends entirely on
 * what these two lines of text say, including for reports missing a booking
 * number, an issue type or a date.
 */
export const buildProblemReportOptions = (
    reports: IssueReport[],
    { t, language }: BuildOptions,
): SearchableSelectOption[] =>
    [...reports]
        .sort((a, b) => createdMillis(b) - createdMillis(a))
        .map((report) => {
            const issueType = report.issueType
                ? getIssueTypeText(report.issueType, t)
                : '';
            const created = report.createdAt
                ? formatDateTime(report.createdAt, language, { includeTime: true })
                : t('common.not_applicable');
            const creator = report.userCreatorName || report.userCreatorEmail || '';
            // The first line needs something that actually identifies the row.
            // Booking number when there is a real one — it is what customer
            // service works from. Otherwise the creation date: without it,
            // several reports of the same kind ("Equipment-Check: Dry Bag
            // fehlt") render identically and cannot be told apart, which is
            // exactly what made the old list unusable.
            const booking = identifyingBookingNumber(report);
            const identifier = booking ? `#${booking}` : created;
            return {
                value: report.id,
                label: [identifier, issueType || report.message || report.id.slice(0, 8)]
                    .filter(Boolean)
                    .join(' · '),
                // Don't repeat the date when it is already the identifier —
                // horizontal space is scarce, especially on a phone.
                description: [...(booking ? [created] : []), creator, report.message]
                    .filter(Boolean)
                    .join(' · '),
                // Searchable but not shown — lets the agent paste an id or
                // look a report up by state / service.
                keywords: [report.id, report.state, report.service, report.userCreatorEmail]
                    .filter(Boolean)
                    .join(' '),
            };
        });
